import { api } from './axios';

/**
 * Валидация ID
 * @param {string|number} id - Идентификатор
 * @param {string} name - Название поля для ошибки
 */
const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с пользователями
 */
export const usersApi = {
    /**
     * Получает текущего пользователя
     * @param {AbortSignal} [signal] - Сигнал отмены запроса
     * @returns {Promise<Object>}
     */
    current: async (signal = null) => {
        try {
            const { data } = await api.get('/users/current', { signal });
            return data;
        } catch (error) {
            console.error('[usersApi.current] Failed to fetch current user:', error.message);
            throw error;
        }
    },

    /**
     * Получает список пользователей
     * @param {AbortSignal} [signal] - Сигнал отмены запроса
     * @returns {Promise<Array>}
     */
    list: async (signal = null) => {
        try {
            const { data } = await api.get('/users', { signal });
            return data;
        } catch (error) {
            console.error('[usersApi.list] Failed to fetch users:', error.message);
            throw error;
        }
    },

    /**
     * Получает пользователя по ID
     * @param {string|number} id - ID пользователя
     * @param {AbortSignal} [signal] - Сигнал отмены запроса
     * @returns {Promise<Object>}
     */
    get: async (id, signal = null) => {
        validateId(id, 'User ID');
        try {
            const { data } = await api.get(`/users/${id}`, { signal });
            return data;
        } catch (error) {
            console.error(`[usersApi.get] Failed to fetch user ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновляет пользователя
     * @param {string|number} id - ID пользователя
     * @param {Object} payload - Данные для обновления
     * @returns {Promise<Object>}
     */
    update: async (id, payload) => {
        validateId(id, 'User ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/users/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[usersApi.update] Failed to update user ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает текущего пользователя (alias /me)
     * @returns {Promise<Object>}
     */
    me: async () => {
        try {
            const { data } = await api.get('/users/me');
            return data;
        } catch (error) {
            console.error('[usersApi.me] Failed to fetch current user:', error.message);
            throw error;
        }
    },

    /**
     * Получает список всех пользователей с пагинацией и фильтрами (ADMIN)
     * @param {Object} params - Параметры (page, size, sortBy, sortDir, username, email, role, gender, city, isBanned)
     * @returns {Promise<Object>}
     */
    listAll: async (params = {}) => {
        try {
            const { data } = await api.get('/users/all', { params });
            return data;
        } catch (error) {
            console.error('[usersApi.listAll] Failed to fetch users:', error.message);
            throw error;
        }
    },

    /**
     * Обновляет данные текущего пользователя (PATCH /users/me)
     * @param {Object} payload - Данные для обновления
     * @returns {Promise<Object>}
     */
    updateMe: async (payload) => {
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.patch('/users/me', payload);
            return data;
        } catch (error) {
            console.error('[usersApi.updateMe] Failed to update current user:', error.message);
            throw error;
        }
    },

    /**
     * Забанить пользователя (ADMIN)
     * @param {string|number} id - ID пользователя
     * @param {Object} payload - reason, durationDays
     * @returns {Promise<Object>}
     */
    ban: async (id, payload) => {
        validateId(id, 'User ID');
        try {
            const { data } = await api.post(`/users/${id}/ban`, payload);
            return data;
        } catch (error) {
            console.error(`[usersApi.ban] Failed to ban user ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Разбанить пользователя (ADMIN)
     * @param {string|number} id - ID пользователя
     * @returns {Promise<Object>}
     */
    unban: async (id) => {
        validateId(id, 'User ID');
        try {
            const { data } = await api.post(`/users/${id}/unban`);
            return data;
        } catch (error) {
            console.error(`[usersApi.unban] Failed to unban user ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Подписаться на пользователя
     * @param {string|number} userId - ID пользователя
     * @param {string|number} followerId - ID подписчика
     * @returns {Promise<Object>}
     */
    follow: async (userId, followerId) => {
        validateId(userId, 'User ID');
        validateId(followerId, 'Follower ID');
        try {
            const { data } = await api.post(`/users/${userId}/follow`, null, { params: { followerId } });
            return data;
        } catch (error) {
            console.error(`[usersApi.follow] Failed to follow user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Отписаться от пользователя
     * @param {string|number} userId - ID пользователя
     * @param {string|number} followerId - ID подписчика
     * @returns {Promise<Object>}
     */
    unfollow: async (userId, followerId) => {
        validateId(userId, 'User ID');
        validateId(followerId, 'Follower ID');
        try {
            const { data } = await api.post(`/users/${userId}/unfollow`, null, { params: { followerId } });
            return data;
        } catch (error) {
            console.error(`[usersApi.unfollow] Failed to unfollow user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить подписчиков пользователя
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Array>}
     */
    getFollowers: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/users/${userId}/followers`);
            return data;
        } catch (error) {
            console.error(`[usersApi.getFollowers] Failed to fetch followers for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить подписки пользователя
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Array>}
     */
    getFollowing: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/users/${userId}/following`);
            return data;
        } catch (error) {
            console.error(`[usersApi.getFollowing] Failed to fetch following for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить количество подписчиков
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<number>}
     */
    getFollowersCount: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/users/${userId}/followers/count`);
            return data;
        } catch (error) {
            console.error(`[usersApi.getFollowersCount] Failed to fetch followers count for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить количество подписок
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<number>}
     */
    getFollowingCount: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/users/${userId}/following/count`);
            return data;
        } catch (error) {
            console.error(`[usersApi.getFollowingCount] Failed to fetch following count for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Проверить, подписан ли follower на пользователя
     * @param {string|number} userId - ID пользователя
     * @param {string|number} followerId - ID подписчика
     * @returns {Promise<boolean>}
     */
    isFollowing: async (userId, followerId) => {
        validateId(userId, 'User ID');
        validateId(followerId, 'Follower ID');
        try {
            const { data } = await api.get(`/users/${userId}/is-following`, { params: { followerId } });
            return data;
        } catch (error) {
            console.error(`[usersApi.isFollowing] Failed to check following status for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет пользователя (soft delete, ADMIN)
     * @param {string|number} id - ID пользователя
     * @returns {Promise<Object>} Ответ от сервера
     */
    remove: async (id) => {
        validateId(id, 'User ID');
        try {
            const { data } = await api.delete(`/users/${id}`);
            return data;
        } catch (error) {
            console.error(`[usersApi.remove] Failed to delete user ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получить настройки текущего пользователя (каналы уведомлений)
     * GET /users/me/settings
     * @returns {Promise<Object>} { emailNotificationsEnabled, pushNotificationsEnabled, inAppNotificationsEnabled }
     */
    getSettings: async () => {
        try {
            const { data } = await api.get('/users/me/settings');
            return data;
        } catch (error) {
            console.error('[usersApi.getSettings] Failed to fetch user settings:', error.message);
            throw error;
        }
    },

    /**
     * Обновить настройки текущего пользователя (каналы уведомлений)
     * PATCH /users/me/settings
     * @param {Object} payload - { emailNotificationsEnabled?, pushNotificationsEnabled?, inAppNotificationsEnabled? }
     * @returns {Promise<Object>} Обновлённые настройки
     */
    updateSettings: async (payload) => {
        if (!payload) throw new Error('Settings payload is required');
        try {
            const { data } = await api.patch('/users/me/settings', payload);
            return data;
        } catch (error) {
            console.error('[usersApi.updateSettings] Failed to update user settings:', error.message);
            throw error;
        }
    }
};